/* eslint-disable complexity */
import { ErrorBanner } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { TextInput, LoadingSpinner, Switch, Badge, Dialog, Select } from '@soteria/design-system-ve';
import { useCreateStandardLabel, useUpdateStandardLabel } from '@soteria-ui/src/system-config/label-management/hooks';
import { LabelStatus, ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useEffect, useState } from 'react';

import * as styles from './StandardLabelDialog.css';

interface StandardLabelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  label?: StandardLabel;
}

interface FormData {
  labelName: string;
  labelShortName: string;
  policy: string;
  contentType: ContentType;
  labelNumMap?: number;
  labelStatus: LabelStatus;
}

function StandardLabelDialogComponent({ isOpen, onClose, label }: StandardLabelDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    labelName: '',
    labelShortName: '',
    policy: '',
    contentType: ContentType.TEXT,
    labelNumMap: undefined,
    labelStatus: LabelStatus.PENDING,
  });

  useEffect(() => {
    // Reset form data when dialog opens or label changes
    if (isOpen) {
      setFormData({
        labelName: label?.labelName || '',
        labelShortName: label?.labelShortName || '',
        policy: label?.policy || '',
        contentType: label?.contentType || ContentType.TEXT,
        labelNumMap: label?.labelNumMap,
        labelStatus: label?.labelStatus || LabelStatus.PENDING,
      });
    }
  }, [isOpen, label]);

  const { createStandardLabel, loading: isCreating, errors: createErrors, resetErrors: resetCreateErrors } = useCreateStandardLabel();
  const { updateStandardLabel, loading: isUpdating, errors: updateErrors, resetErrors: resetUpdateErrors } = useUpdateStandardLabel();

  const isLoading = isCreating || isUpdating;
  const errors = createErrors || updateErrors;

  const isFormValid = () => {
    // Check required fields
    const requiredFieldsValid = formData.labelName.trim() !== '' && formData.policy.trim() !== '' && formData.labelNumMap !== undefined;

    // If editing, also check if anything changed
    if (label) {
      const hasChanges =
        formData.labelName !== label.labelName ||
        formData.labelShortName !== label.labelShortName ||
        formData.policy !== label.policy ||
        formData.contentType !== (label.contentType || ContentType.TEXT) ||
        formData.labelNumMap !== label.labelNumMap ||
        formData.labelStatus !== label.labelStatus;

      return requiredFieldsValid && hasChanges;
    }

    return requiredFieldsValid;
  };

  const handleInputChange = (field: keyof FormData) => (value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    resetCreateErrors();
    resetUpdateErrors();

    if (label) {
      // Update existing label
      const success = await updateStandardLabel(label.id, formData);

      if (success) {
        onClose();
      }
    } else {
      // Create new label with required scope
      const success = await createStandardLabel({
        labelName: formData.labelName,
        labelShortName: formData.labelShortName,
        policy: formData.policy,
        contentType: formData.contentType,
        labelNumMap: formData.labelNumMap as number,
        scope: {
          global: true,
          communities: [],
          communityGroups: [],
        },
      });

      if (success) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      cancelAction={{ label: 'Close' }}
      customHeader={
        label ? (
          <div className={styles.customHeader}>
            <div className={styles.titleWithBadge}>
              <h2 className={styles.dialogTitle}>{label.labelName}</h2>
              <Badge
                className={label.labelStatus === LabelStatus.APPROVED ? styles.approvedBadge : styles.pendingBadge}
                text={label.labelStatus.toUpperCase()}
              />
            </div>
            <p className={styles.dialogDescription}>Edit the {label.labelName} standard label</p>
          </div>
        ) : undefined
      }
      description={!label ? 'Create a new standard label' : undefined}
      isOpen={isOpen}
      primaryAction={{
        label: isLoading ? <LoadingSpinner /> : label ? 'Save Changes' : 'Add Label',
        onClick: handleSubmit,
        disabled: isLoading || !isFormValid(),
      }}
      size="md"
      title={!label ? 'Add Standard Label' : undefined}
      onClose={() => {
        resetCreateErrors();
        resetUpdateErrors();
        onClose();
      }}
    >
      <div className={styles.form}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the label:" />}

        {/* Label Status Toggle - Only show when editing */}
        {label && (
          <div className={styles.toggleContainer}>
            <span className={styles.toggleLabel}>Approved</span>
            <Switch
              checked={formData.labelStatus === LabelStatus.APPROVED}
              id="labelStatus"
              label="Toggle label approval status"
              onChange={(checked: boolean) => {
                handleInputChange('labelStatus')(checked ? LabelStatus.APPROVED : LabelStatus.PENDING);
              }}
            />
          </div>
        )}

        {/* Label Name Input */}
        <TextInput
          required
          id="labelName"
          label="Label Name"
          placeholder="Enter label name"
          value={formData.labelName}
          onChange={handleInputChange('labelName')}
        />

        {/* Label Short Name Input */}
        <TextInput
          id="labelShortName"
          label="Label Short Name"
          placeholder="Enter short name"
          value={formData.labelShortName}
          onChange={handleInputChange('labelShortName')}
        />

        {/* Policy File Name Input */}
        <TextInput
          required
          id="policy"
          label="Policy File Name"
          placeholder="Enter policy file name"
          value={formData.policy}
          onChange={handleInputChange('policy')}
        />

        {/* Content Type Dropdown */}
        <Select
          required
          label="Content Type"
          options={[
            { value: ContentType.TEXT, label: 'Text' },
            { value: ContentType.IMAGE, label: 'Image' },
          ]}
          value={formData.contentType}
          onChange={handleInputChange('contentType')}
        />

        {/* Numeric Mapping Input */}
        <TextInput
          required
          id="labelNumMap"
          label="Numeric Mapping"
          placeholder="Enter numeric mapping"
          type="number"
          value={formData.labelNumMap?.toString() || ''}
          onChange={value => {
            const parsedValue = value ? parseInt(value, 10) : undefined;

            handleInputChange('labelNumMap')(parsedValue);
          }}
        />
      </div>
    </Dialog>
  );
}

export const StandardLabelDialog = withAppInsightsTracking(StandardLabelDialogComponent, 'StandardLabelDialog');
